import Cookies from 'js-cookie';

const CLIENT_ID = 'bd217210cfc345d380d26d13c38aface';
const REDIRECT_URI = window.location.origin;
const SCOPES = [
  'user-read-private',
  'user-read-email',
  'playlist-modify-private',
  'playlist-modify-public'
];

// Generate a random string for PKCE
function generateRandomString(length) {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let text = '';
  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
}

// Generate code verifier for PKCE
function generateCodeVerifier() {
  return generateRandomString(128);
}

// Generate code challenge from verifier
async function generateCodeChallenge(verifier) {
  const data = new TextEncoder().encode(verifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode.apply(null, [...new Uint8Array(digest)]))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// Initiate Spotify OAuth with PKCE
export async function initiateSpotifyAuth() {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  
  // Store code verifier in sessionStorage for later use
  sessionStorage.setItem('code_verifier', codeVerifier);
  
  // Generate state for CSRF protection
  const state = generateRandomString(16);
  sessionStorage.setItem('auth_state', state);
  
  const scope = SCOPES.join(' ');
  const args = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    scope: scope,
    redirect_uri: REDIRECT_URI,
    state: state,
    code_challenge_method: 'S256',
    code_challenge: codeChallenge,
  });
  
  window.location = 'https://accounts.spotify.com/authorize?' + args;
}

// Exchange authorization code for access token
export async function exchangeCodeForToken(code) {
  const codeVerifier = sessionStorage.getItem('code_verifier');
  
  if (!codeVerifier) {
    throw new Error('Code verifier not found');
  }
  
  const response = await fetch('https://accounts.spotify.com/api/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: REDIRECT_URI,
      client_id: CLIENT_ID,
      code_verifier: codeVerifier,
    }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error_description || 'Failed to exchange code for token');
  }
  
  const data = await response.json();
  
  // Store token in cookie (matching existing behavior)
  Cookies.set('spotifyAuthToken', data.access_token, { expires: 1 }); // 1 day expiry
  
  // Clean up session storage
  sessionStorage.removeItem('code_verifier');
  sessionStorage.removeItem('auth_state');
  
  return data.access_token;
}

// Check if we're returning from OAuth callback
export function handleAuthCallback() {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');
  const error = urlParams.get('error');
  
  if (error) {
    console.error('Spotify auth error:', error);
    return { error };
  }
  
  if (code && state) {
    const storedState = sessionStorage.getItem('auth_state');
    if (state !== storedState) {
      return { error: 'State mismatch' };
    }
    
    return { code };
  }
  
  return null;
}

// Clear auth data
export function logout() {
  Cookies.remove('spotifyAuthToken');
  sessionStorage.removeItem('code_verifier');
  sessionStorage.removeItem('auth_state');
}

